import { KeyboardEvent, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { nameof } from "ts-simple-nameof";
import { AppDialog } from "../../../../../common/components/AppDialog/AppDialog";
import { DefaultButton } from "../../../../../common/components/button/defaultButton/DefaultButton";
import { DefaultInputField } from "../../../../../common/components/forms/inputs/InputField/DefaultInputField";
import { DefaultToggle } from "../../../../../common/components/forms/inputs/toggle/DefaultToggle";
import {
  IStudioQrCodeFormModel,
  studioQrCodeFormDefaultValues,
  studioQrCodeSchema,
} from "./models/studioQrCodeFormModel";
import { IStudioQrCode } from "./models/studioQrCodeModels";
import styles from "./StudioQrCodesSection.module.scss";

interface IStudioQrCodeDialogProps {
  visible: boolean;
  onHide: () => void;
  editing?: IStudioQrCode | null;
  isSaving: boolean;
  onSave: (values: IStudioQrCodeFormModel) => void;
}

export const StudioQrCodeDialog = ({
  visible,
  onHide,
  editing,
  isSaving,
  onSave,
}: IStudioQrCodeDialogProps) => {
  const form = useForm<IStudioQrCodeFormModel>({
    resolver: yupResolver(studioQrCodeSchema),
    defaultValues: studioQrCodeFormDefaultValues,
  });

  // Re-seed the form each time the dialog opens (create => blank, edit => existing values).
  useEffect(() => {
    if (!visible) {
      return;
    }

    if (editing) {
      form.reset({
        id: editing.id,
        name: editing.name,
        destinationUrl: editing.destinationUrl,
        isActive: editing.isActive,
      });
    } else {
      form.reset(studioQrCodeFormDefaultValues);
    }
  }, [visible, editing, form]);

  const isEdit = !!editing;

  // NOTE: this dialog is rendered inside the Studio wizard's <form>. Using a nested <form>
  // here is invalid HTML — the browser drops it and a submit button ends up submitting the
  // wizard form (saving the whole studio). So we submit via an explicit handler on a
  // type="button" button and guard Enter from bubbling up to the wizard form.
  const submit = form.handleSubmit(onSave);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
      submit();
    }
  };

  return (
    <AppDialog
      visible={visible}
      onHide={onHide}
      header={isEdit ? "Редакция на QR код" : "Нов QR код"}
      tooltip="QR кодът сочи към кратък адрес на Planny, който препраща към зададената дестинация. Дестинацията може да се сменя по всяко време, без да се преотпечатва кодът."
    >
      <FormProvider {...form}>
        <div className={styles.dialog_form} onKeyDown={handleKeyDown}>
          <DefaultInputField
            name={nameof<IStudioQrCodeFormModel>((x) => x.name)}
            label="Име"
            placeholder="напр. Плакат витрина"
            required
          />

          <DefaultInputField
            name={nameof<IStudioQrCodeFormModel>((x) => x.destinationUrl)}
            label="Дестинация (адрес, към който препраща)"
            placeholder="напр. https://instagram.com/mystudio"
            required
            tooltip="Пълният адрес, към който води QR кодът при сканиране. Ако пропуснете http(s), ще се добави автоматично https://."
          />

          <DefaultToggle
            name={nameof<IStudioQrCodeFormModel>((x) => x.isActive)}
            label="Активен"
            tooltip="Когато е изключен, сканиранията не препращат към дестинацията."
          />

          <div className={styles.dialog_actions}>
            <DefaultButton
              label="Отказ"
              design="link"
              type="button"
              width="auto"
              onClick={onHide}
            />
            <DefaultButton
              label={isEdit ? "Запази" : "Създай"}
              type="button"
              width="auto"
              loading={isSaving}
              onClick={() => submit()}
            />
          </div>
        </div>
      </FormProvider>
    </AppDialog>
  );
};
